import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { EQM1030_R01Dto } from './Dto_EQM1030_R01';

export interface EQM1030_R01Filter {
    compCd: string;
    baseDate: Date | undefined;
}

// 탭 타입 정의
export type TabType = 'chart' | 'table';

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_EQM1030_R01 {
    // === 탭 관련 ===
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    EQM1030_R01Filter: EQM1030_R01Filter;
    EQM1030_R01PastFilter: EQM1030_R01Filter;
    setEQM1030_R01Filter: (filter: Partial<EQM1030_R01Filter>) => void;
    setEQM1030_R01PastFilter: (filter: Partial<EQM1030_R01Filter>) => void;

    // === 리스트 관련 ===
    EQM1030_R01List: EQM1030_R01Dto[];
    setEQM1030_R01List: (list: EQM1030_R01Dto[]) => void;
    insertEQM1030_R01List: (data: EQM1030_R01Dto[], index?: number) => void;
    removeEQM1030_R01List: (index: number) => void;
    updateEQM1030_R01List: (data: EQM1030_R01Dto, index: number) => void;
    
    // === 선택 관련 ===
    selectedEQM1030_R01List: string[];
    setSelectedEQM1030_R01List: (idList: string[]) => void;
    addSelectedEQM1030_R01List: (idList: string[]) => void;
    removeSelectedEQM1030_R01List: (idList?: string[]) => void;
    initSelectedEQM1030_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    // === Chart 데이터 관련 ===
    chartData: any[];
    setChartData: (data: any[]) => void;

    // === 최신 업데이트 시간 (현재시간 표시용) ===
    latestUpdateTime: Date | undefined;
    setLatestUpdateTime: (date: Date | undefined) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

export const defaultEQM1030_R01Filter: EQM1030_R01Filter = {
    compCd: '',
    baseDate: new Date(),
};

/**
 * Zustand 스토어 정의
 */
export const useEQM1030_R01Store = create<Store_EQM1030_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_EQM1030_R01, 'EQM1030_R01Filter' | 'EQM1030_R01PastFilter'>) => 
        (filter: Partial<EQM1030_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        // === 탭 관련 ===
        activeTab: 'table',
        setActiveTab: (tab) => set({ activeTab: tab }),

        EQM1030_R01Filter: defaultEQM1030_R01Filter,
        EQM1030_R01PastFilter: defaultEQM1030_R01Filter,
        setEQM1030_R01Filter: createFilterSetter('EQM1030_R01Filter'),
        setEQM1030_R01PastFilter: createFilterSetter('EQM1030_R01PastFilter'),

        // === 리스트 관련 ===
        EQM1030_R01List: [],
        setEQM1030_R01List: (list: EQM1030_R01Dto[]) => set({ EQM1030_R01List: list }),
        insertEQM1030_R01List: (data: EQM1030_R01Dto[], index?: number) => set((state) => {
            const _list = [...state.EQM1030_R01List];
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, EQM1030_R01List: _list };
        }),
        removeEQM1030_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            EQM1030_R01List: state.EQM1030_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updateEQM1030_R01List: (data: EQM1030_R01Dto, index: number) => set((state) => {
            const _list = [...state.EQM1030_R01List];
            _list[index] = data;
            return { ...state, EQM1030_R01List: _list };
        }),

        // === 선택 관련 ===
        selectedEQM1030_R01List: [],
        setSelectedEQM1030_R01List: (idList: string[]) => set({ selectedEQM1030_R01List: idList }),
        addSelectedEQM1030_R01List: (idList: string[]) => set((state) => {
            const _selected = [...state.selectedEQM1030_R01List];
            _selected.push(...idList.filter(value => !_selected.includes(value)));
            return { ...state, selectedEQM1030_R01List: _selected };
        }),
        removeSelectedEQM1030_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedEQM1030_R01List: [] };
            }
            return {
                ...state,
                selectedEQM1030_R01List: removeItemsFromArray(state.selectedEQM1030_R01List, idList),
            };
        }),
        initSelectedEQM1030_R01List: () => set({ selectedEQM1030_R01List: [] }),

        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
        // gridCursor: 0,
        setAllCount: (allCount: number) => set({ allCount }),

        // === Chart 데이터 관련 ===
        chartData: [],
        setChartData: (data: any[]) => set({ chartData: data }),

        // === 최신 업데이트 시간 (현재시간 표시용) ===
        latestUpdateTime: undefined,
        setLatestUpdateTime: (date: Date | undefined) => set({ latestUpdateTime: date }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            activeTab: 'table',
            EQM1030_R01Filter: defaultEQM1030_R01Filter,
            EQM1030_R01PastFilter: defaultEQM1030_R01Filter,
            EQM1030_R01List: [],
            selectedEQM1030_R01List: [],
            columnState: null,
            allCount: 0,
            gridCursor: 0,
            chartData: [],
            latestUpdateTime: undefined,
        })),
    };
});


